import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, LayoutGrid, Bot, MessageSquare, ArrowRight, ArrowLeft, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const key = (id) => `cs_tour_seen_${id}`;
const PUBLIC_ROUTES = ["/login", "/signup", "/invite", "/demo", "/"];

export const WelcomeTour = () => {
  const { status, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (status !== "authed" || !user) return;
    if (location.pathname !== "/studio") return; // only auto-open on the main Studio page
    if (!localStorage.getItem(key(user.id))) {
      setStep(0);
      setOpen(true);
    }
  }, [status, user, location.pathname]);

  const onPublic = PUBLIC_ROUTES.some((r) => r === "/" ? location.pathname === "/" : location.pathname.startsWith(r));
  if (!open || !user || onPublic) return null;

  const isEditor = ["owner", "manager"].includes(user.role);
  const steps = isEditor
    ? [
        { icon: Sparkles, title: "Welcome to Customize Studio", body: "Here's a 30-second tour. The Studio is where the magic happens — upload a customer's photo and generate a photorealistic before & after in seconds, right at the appointment.", go: "/studio" },
        { icon: LayoutGrid, title: "Build your Library", body: "The Library is your catalog. New accounts start empty on purpose — add your OWN Services (Cabinet Refacing, Roofing, Siding…), the Features you change on each, and the options & colors for every feature.", go: "/library" },
        { icon: Bot, title: "Meet Roger, your AI helper", body: "Roger can help you name and organize your library from plain English — and suggest services and features to get your business set up fast.", go: "/library" },
        { icon: MessageSquare, title: "We're here to help", body: "Stuck or want ideas? Message your account manager any time from the Messages tab — we'll help you set up your library.", go: "/messages" },
      ]
    : [
        { icon: Sparkles, title: "Welcome to Customize Studio", body: "The Studio is where you work — upload a customer's photo, pick what you're changing, and generate a photorealistic before & after in seconds.", go: "/studio" },
        { icon: MessageSquare, title: "Need a hand?", body: "Questions or ideas? Message your account manager any time from the Messages tab.", go: "/messages" },
      ];

  const last = step === steps.length - 1;
  const s = steps[step];
  const Icon = s.icon;

  const finish = (go) => {
    localStorage.setItem(key(user.id), "1");
    setOpen(false);
    if (go) navigate(go);
  };

  return (
    <div data-testid="welcome-tour" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="relative bg-[var(--ink)] px-6 py-8 text-white">
          <button data-testid="tour-skip" onClick={() => finish(null)} className="absolute right-3 top-3 rounded-full p-2 text-white/70 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand)]">
            <Icon className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <h2 className="mt-4 font-display text-2xl tracking-tight">{s.title}</h2>
        </div>
        <div className="px-6 py-6">
          <p className="text-sm leading-relaxed text-[var(--ink-soft)]">{s.body}</p>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[var(--brand)]" : "w-1.5 bg-[var(--line)]"}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button data-testid="tour-back" onClick={() => setStep((n) => n - 1)}
                  className="flex items-center gap-1 rounded-full border border-[var(--line)] px-4 py-2 text-sm font-medium hover:bg-[var(--surface-2)]">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
              )}
              {last ? (
                <button data-testid="tour-finish" onClick={() => finish(s.go)}
                  className="flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)]">
                  {isEditor ? "Set up my Library" : "Go to Studio"} <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              ) : (
                <button data-testid="tour-next" onClick={() => setStep((n) => n + 1)}
                  className="flex items-center gap-1.5 rounded-full bg-[var(--brand)] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)]">
                  Next <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
